'use client'

import { useState } from 'react'
import { IconBookmark, IconChevronDown } from '@tabler/icons-react'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover'
import { useBookmark } from '@/hooks/useBookmark'
import { LoginDialog } from '@/components/auth/login-dialog'
import { useAuth } from '@/hooks/useAuth'
import { cn } from '@/lib/utils'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getUserCollections, addEntryToCollection } from '@/lib/collections'
import { toast } from 'sonner'
import { useTranslations } from 'next-intl'
import { Separator } from '@/components/ui/separator'
import { BodyText } from '@/components/ui/typography'
import { CreateCollectionDialog } from '@/components/collections/create-collection-dialog'

type CollectionButtonProps = {
  entryId: number
  size?: 'sm' | 'md' | 'lg'
  variant?: 'ghost' | 'outline'
  className?: string
  label?: string
}

const sizeMap = {
  sm: 'h-4 w-4',
  md: 'size-6',
  lg: 'size-6'
}

const buttonSizeMap = {
  sm: 'icon-sm',
  md: 'icon-lg',
  lg: 'icon-lg'
} as const

export function CollectionButton({
  entryId,
  size = 'md',
  variant = 'ghost',
  className,
  label
}: CollectionButtonProps) {
  const { user } = useAuth()
  const t = useTranslations('entry')
  const tCommon = useTranslations('common')
  const queryClient = useQueryClient()
  const {
    isBookmarked,
    isLoading: bookmarkLoading,
    toggleBookmark
  } = useBookmark(entryId)
  const [popoverOpen, setPopoverOpen] = useState(false)

  // Fetch user collections (always fetch, not just when popover opens, so we can show chevron indicator)
  const { data: collections = [], isLoading: collectionsLoading } = useQuery({
    queryKey: ['user-collections', user?.id],
    queryFn: () => {
      if (!user?.id) return []
      return getUserCollections(user.id)
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5 // 5 minutes
  })

  // Add to collection mutation
  const addToCollectionMutation = useMutation({
    mutationFn: async (collectionId: string) => {
      if (!user?.id) throw new Error('User not logged in')
      await addEntryToCollection(user.id, collectionId, entryId)
    },
    onSuccess: (_, collectionId) => {
      const collection = collections.find((c) => c.id === collectionId)
      toast.success(
        tCommon('addedToCollection', {
          collection: collection?.name || 'collection'
        }) || `Added to ${collection?.name || 'collection'}`
      )
      // Invalidate relevant queries
      queryClient.invalidateQueries({
        queryKey: ['user-collections', user?.id]
      })
      queryClient.invalidateQueries({ queryKey: ['all-collections', user?.id] })
      queryClient.invalidateQueries({
        queryKey: ['study-deck-items', collectionId],
        exact: false
      })
      setPopoverOpen(false)
    },
    onError: () => {
      toast.error(t('collectionAddError'))
    }
  })

  const handleCollectionClick = async (collectionId: string) => {
    await addToCollectionMutation.mutateAsync(collectionId)
  }

  // Filter out bookmark collection from the list (it's handled by quick toggle)
  const otherCollections = collections.filter((c) => !c.is_bookmark)

  const buttonContent = label ? (
    <>
      <IconBookmark
        className={cn(sizeMap[size], isBookmarked ? 'fill-current' : '')}
      />
      <span>{label}</span>
      <IconChevronDown className="h-3 w-3 opacity-70" />
    </>
  ) : (
    <>
      <IconBookmark
        className={cn(sizeMap[size], isBookmarked ? 'fill-current' : '')}
      />
      {/* Always show chevron to indicate popover is available */}
      <IconChevronDown className="bg-background absolute right-0 bottom-0 h-2 w-2 rounded-full p-0.5" />
    </>
  )

  const button = (
    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={variant}
          size={label ? 'sm' : buttonSizeMap[size]}
          aria-label={isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
          onClick={(e) => {
            e.stopPropagation()
            setPopoverOpen(true)
          }}
          disabled={bookmarkLoading}
          className={cn('relative shrink-0', label && 'w-full', className)}
        >
          {buttonContent}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-64 p-0"
        align="start"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="p-2">
          <BodyText className="text-muted-foreground mb-2 text-xs font-medium uppercase">
            Add to deck
          </BodyText>

          {/* Bookmark toggle option - always shown */}
          <button
            onClick={async (e) => {
              e.stopPropagation()
              await toggleBookmark()
              setPopoverOpen(false)
            }}
            disabled={bookmarkLoading}
            className="hover:bg-accent flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors disabled:opacity-50"
          >
            <IconBookmark
              className={cn('h-4 w-4', isBookmarked ? 'fill-current' : '')}
            />
            <span>
              {isBookmarked ? 'Remove from Saved Words' : 'Add to Saved Words'}
            </span>
          </button>

          {/* Other collections */}
          {collectionsLoading ? (
            <BodyText className="text-muted-foreground py-4 text-center text-sm">
              {tCommon('loading')}...
            </BodyText>
          ) : otherCollections.length > 0 ? (
            <>
              <Separator className="my-2" />
              <div className="space-y-1">
                {otherCollections.map((collection) => (
                  <button
                    key={collection.id}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleCollectionClick(collection.id)
                    }}
                    disabled={addToCollectionMutation.isPending}
                    className="hover:bg-accent flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-sm transition-colors disabled:opacity-50"
                  >
                    <span>{collection.name}</span>
                    {collection.item_count > 0 && (
                      <span className="text-muted-foreground text-xs">
                        {collection.item_count}
                      </span>
                    )}
                  </button>
                ))}
                <CreateCollectionDialog
                  trigger={
                    <button className="hover:bg-accent flex w-full items-center rounded-md px-2 py-1.5 text-left text-sm transition-colors">
                      Create deck
                    </button>
                  }
                  onSuccess={() => {
                    queryClient.invalidateQueries({
                      queryKey: ['user-collections', user?.id]
                    })
                  }}
                />
              </div>
            </>
          ) : (
            <>
              <Separator className="my-2" />
              <BodyText className="text-muted-foreground py-2 text-center text-xs">
                No other decks yet.
              </BodyText>
              <CreateCollectionDialog
                trigger={
                  <button className="hover:bg-accent flex w-full items-center justify-center rounded-md px-2 py-1.5 text-sm transition-colors">
                    Create deck
                  </button>
                }
              />
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )

  if (!user) {
    return (
      <LoginDialog>
        <Button
          variant={variant}
          size={label ? 'sm' : buttonSizeMap[size]}
          aria-label="Sign in to save word"
          className={cn('relative shrink-0', label && 'w-full', className)}
        >
          <IconBookmark className={cn(sizeMap[size])} />
          {label && <span>{label}</span>}
        </Button>
      </LoginDialog>
    )
  }

  return button
}
